import React, { useMemo, useCallback } from 'react';

import { Grid } from '@gogame/components';
import {
  FormField,
  useSiblingFieldName,
  withFormFieldGroupFormField,
} from '@gogame/form';

import { useExtraContext } from 'scenes/SmartRules/SmartRuleForm/ExtraContext';
import { ADVERTISING_TARGET } from 'scenes/SmartRules/SmartRuleForm/constants';
import {
  loadAdSets,
  loadAffiliates,
  loadApplications,
  loadCampaigns,
  loadCountries,
  loadLabels,
  loadNetworks,
  loadOwners,
  loadPartnerCampaigns,
  loadPlatforms,
  loadChannels,
  loadMasterCampaigns,
  loadSocialMediaPlatforms,
  loadInfluencerCampaigns,
  loadTrafficTypes,
} from 'scenes/SmartRules/sources/fields';

import {
  AD_SETS,
  AFFILIATES,
  APPLICATIONS,
  CAMPAIGNS,
  COUNTRIES,
  FIELDS_BY_AFFECTED_LEVEL,
  LABELS,
  NETWORKS,
  OWNERS,
  PARTNER_CAMPAIGNS,
  PLATFORMS,
  CHANNELS,
  INFLUENCER_CAMPAIGNS,
  MASTER_CAMPAIGNS,
  SOCIAL_MEDIA_PLATFORMS,
  TRAFFIC_TYPE,
} from './constants';
import { validationSchema } from './schema';

export const AdvertisingPopulations = withFormFieldGroupFormField({
  name: ADVERTISING_TARGET,
  validationSchema: validationSchema,
})(({ xs = 4, value }) => {
  const { dimension, advertisingPopulationFields } = useExtraContext();
  const getSiblingFieldName = useSiblingFieldName();

  const isAllowField = useCallback(
    (name) => {
      return FIELDS_BY_AFFECTED_LEVEL?.[dimension]?.includes(name) || false;
    },
    [advertisingPopulationFields, dimension]
  );
  const networksIsFilled = useMemo(
    () => value?.[NETWORKS]?.length > 0,
    [value?.[NETWORKS]]
  );
  const masterCampaignsIsFilled = useMemo(
    () => value?.[MASTER_CAMPAIGNS]?.length > 0
  );

  const _valueRef = React.useRef(null);

  _valueRef.current = value;

  const loadCampaignsWithSelectedIds = useMemo(() => {
    return (params) =>
      loadCampaigns({ ...params, selectedIds: _valueRef.current?.[CAMPAIGNS] });
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={xs}>
        <FormField.MultiSelect
          label={'Owners'}
          placeholder={'All'}
          name={OWNERS}
          source={loadOwners}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      <Grid item xs={xs}>
        <FormField.MultiSelect
          label={'Networks'}
          placeholder={'All'}
          name={NETWORKS}
          source={loadNetworks}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      <Grid item xs={xs}>
        <FormField.MultiSelect
          label={'Platforms'}
          placeholder={'All'}
          name={PLATFORMS}
          source={loadPlatforms}
          depends={[
            getSiblingFieldName(APPLICATIONS),
            getSiblingFieldName(NETWORKS),
          ]}
          active={networksIsFilled}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      <Grid item xs={xs}>
        <FormField.MultiSelect
          label={'Applications'}
          placeholder={'All'}
          name={APPLICATIONS}
          source={loadApplications}
          depends={[getSiblingFieldName(NETWORKS)]}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      <Grid item xs={xs}>
        <FormField.MultiSelect
          label={'Channels'}
          placeholder={'All'}
          name={CHANNELS}
          source={loadChannels}
          depends={[getSiblingFieldName(AFFILIATES)]}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      <Grid item xs={xs}>
        <FormField.MultiSelect
          label={'Affiliates'}
          placeholder={'All'}
          name={AFFILIATES}
          source={loadAffiliates}
          depends={[
            getSiblingFieldName(NETWORKS),
            getSiblingFieldName(OWNERS),
            getSiblingFieldName(PLATFORMS),
            getSiblingFieldName(CHANNELS),
          ]}
          active={networksIsFilled}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      {isAllowField(LABELS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Labels'}
            placeholder={'All'}
            name={LABELS}
            source={loadLabels}
            active={networksIsFilled}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      {isAllowField(COUNTRIES) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Countries'}
            placeholder={'All'}
            name={COUNTRIES}
            source={loadCountries}
            active={networksIsFilled}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      <Grid item xs={xs}>
        <FormField.Select
          label={'Traffic Type'}
          placeholder={'All'}
          name={TRAFFIC_TYPE}
          source={loadTrafficTypes}
          lazy={true}
          padding={0}
          _cleanOnUnmount={true}
        />
      </Grid>
      {isAllowField(CAMPAIGNS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Campaigns'}
            placeholder={'All'}
            name={CAMPAIGNS}
            source={loadCampaignsWithSelectedIds}
            depends={[
              getSiblingFieldName(APPLICATIONS),
              getSiblingFieldName(NETWORKS),
              getSiblingFieldName(PLATFORMS),
              getSiblingFieldName(OWNERS),
              getSiblingFieldName(AFFILIATES),
            ]}
            active={networksIsFilled}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      {isAllowField(PARTNER_CAMPAIGNS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Partner Campaigns'}
            placeholder={'All'}
            name={PARTNER_CAMPAIGNS}
            source={loadPartnerCampaigns}
            depends={[
              getSiblingFieldName(APPLICATIONS),
              getSiblingFieldName(NETWORKS),
              getSiblingFieldName(PLATFORMS),
              getSiblingFieldName(AFFILIATES),
              getSiblingFieldName(CAMPAIGNS),
            ]}
            active={networksIsFilled}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      {isAllowField(AD_SETS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'AdSets'}
            placeholder={'All'}
            name={AD_SETS}
            source={loadAdSets}
            depends={[
              getSiblingFieldName(APPLICATIONS),
              getSiblingFieldName(NETWORKS),
              getSiblingFieldName(PLATFORMS),
              getSiblingFieldName(AFFILIATES),
              getSiblingFieldName(CAMPAIGNS),
              getSiblingFieldName(PARTNER_CAMPAIGNS),
            ]}
            active={networksIsFilled}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      {isAllowField(MASTER_CAMPAIGNS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Master Campaigns'}
            placeholder={'All'}
            name={MASTER_CAMPAIGNS}
            source={loadMasterCampaigns}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      {isAllowField(SOCIAL_MEDIA_PLATFORMS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Social Media Platforms'}
            placeholder={'All'}
            name={SOCIAL_MEDIA_PLATFORMS}
            source={loadSocialMediaPlatforms}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
      {isAllowField(INFLUENCER_CAMPAIGNS) && (
        <Grid item xs={xs}>
          <FormField.MultiSelect
            label={'Influencer Campaigns'}
            placeholder={'All'}
            name={INFLUENCER_CAMPAIGNS}
            source={loadInfluencerCampaigns}
            active={masterCampaignsIsFilled}
            depends={[
              getSiblingFieldName(APPLICATIONS),
              getSiblingFieldName(PLATFORMS),
              getSiblingFieldName(COUNTRIES),
              getSiblingFieldName(NETWORKS),
              getSiblingFieldName(AFFILIATES),
              getSiblingFieldName(MASTER_CAMPAIGNS),
            ]}
            lazy={true}
            padding={0}
            _cleanOnUnmount={true}
          />
        </Grid>
      )}
    </Grid>
  );
});
